import { Component, computed, inject } from "@angular/core";
import { PopupService } from "../popup.service";
import { CompareObjectsService } from "../../compare-objects/compare-objects.service";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { ConversionService } from "@services/conversion.service";
import { DecimalPipe } from "@angular/common";
import { OptionService } from "../../option/option.service";
import { PortfolioLocationObject } from "../../../shared/contract/Portfolio/PortfolioLocation";
import { CompareObject } from "../../../shared/contract/AVM/CompareObject/CompareObject";
import { InfoTextService } from "@services/info-text.service";
import { MapFeatureInfoService } from "@services/map-feature-info.service";

import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { ResidentialPopupInfoComponent } from "../residential-popup-info/residential-popup-info.component";
import { PortfolioPopupInfoComponent } from "../portfolio-popup-info/portfolio-popup-info.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatButtonModule } from "@angular/material/button";
import { PopupMode } from "@enums/popup-mode.enum";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";

@Component({
  selector: "app-popup",
  templateUrl: "./popup.component.html",
  styleUrls: ["./popup.component.scss"],
  imports: [
    FormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatButtonModule,
    ResidentialPopupInfoComponent,
    PortfolioPopupInfoComponent,
    TranslocoPipe
  ]
})
export class PopupComponent {
  popupService = inject(PopupService);
  mapFeatureInfoService = inject(MapFeatureInfoService);
  conversionService = inject(ConversionService);
  private readonly compareObjectService = inject(CompareObjectsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly optionService = inject(OptionService);
  private readonly infoTextService = inject(InfoTextService);
  private readonly thematicInfoService = inject(ThematicInfoService);

  readonly PopupMode = PopupMode;
  private readonly ObjectInfoText = [
    { Name: "ConstructionYear", Property: "ConstructionYear" },
    { Name: "LivingSpace", Property: "LivingSpace" },
    { Name: "ObjectType", Property: "ObjectSubType" },
    { Name: "PlotSize", Property: "PlotSize" },
    { Name: "PlotType", Property: "PlotType" }
  ];

  protected titleText = computed(() => {
    const mode = this.popupService.mode();
    const isLayerPopup = this.popupService.isLayerPopup();

    if (isLayerPopup) {
      const activeLayer = this.popupService.activeLayer();
      const infoText = this.infoTextService.getInfoTextByLocale(activeLayer?.displayName);
      return infoText?.[0]?.text ?? "";
    }

    const selectedObjectDetails = this.popupService.selectedPortfolioDetails();
    const selectedPortfolioObject = this.popupService.selectedPortfolioObject();
    if (!mode) {
      return "";
    }

    switch (mode) {
      case PopupMode.Portfolio: {
        if (!selectedPortfolioObject && !selectedObjectDetails) {
          return "";
        }
        const address = selectedPortfolioObject ? selectedPortfolioObject.address : selectedObjectDetails.address;
        return address.street + " " + address.houseNumber;
      }
      case PopupMode.ResidentialRealEstate:
      case PopupMode.CommercialRealEstate:
        return this.translocoService.translate("Title.MainTitle.CompareObject");
    }
  });

  protected buttonText = computed(() => {
    const mode = this.popupService.mode();
    switch (mode) {
      case PopupMode.Portfolio:
        return "Button.OpenPortfolioObject";
      case PopupMode.ResidentialRealEstate:
      case PopupMode.CommercialRealEstate:
        return "Button.Adaptation";
    }
  });

  protected priceTitle = computed(() => {
    const selectedObject = this.popupService.selectedCompareObject();
    const isRent = this.thematicInfoService.isRent();
    if (!selectedObject) {
      return "-";
    }

    if (isRent) {
      return "Properties.CompareObjectType.Rent." + selectedObject.compareType;
    }

    return "Properties.CompareObjectType.Purchase." + selectedObject.compareType;
  });

  protected priceValue = computed(() => {
    const selectedObject = this.popupService.selectedCompareObject();
    if (!selectedObject) {
      return null;
    }
    return selectedObject.prices.original.price;
  });

  protected setPropertyText(property: string): string {
    if (this.ObjectInfoText.find(e => e.Property === property) !== undefined) {
      return this.translocoService.translate("Module.ObjectInformation." + this.ObjectInfoText.find(e => e.Property === property)?.Name);
    } else if (property === "Purchase" || property === "Rent") {
      return this.translocoService.translate(
        "Properties.CompareObjectType." + property + "." + this.popupService.selectedCompareObject().compareType
      );
    } else if (property === "TransactionYear") {
      return this.translocoService.translate("Table.Header.SalesYear");
    } else if (property === "CommercialType") {
      return this.translocoService.translate("Module.ObjectInformation.ObjectType");
    }
  }

  protected setProperty(property: string): string {
    const selectedObject = this.popupService.selectedCompareObject();

    if (!selectedObject) {
      return "";
    }

    if (property === "ObjectSubType") {
      return (
        this.translocoService.translate(
          "Properties.ObjectType.LongVersion." +
            this.conversionService.convertObjectSubTypeToObjectType(
              selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"]
                .objectSubType
            )
        ) +
        " (" +
        this.translocoService.translate(
          "Properties.ObjectSubType.LongVersion." +
            selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"].objectSubType
        ) +
        ")"
      );
    } else if (property === "CommercialType") {
      return this.translocoService.translate(
        "Properties.ObjectSubType.LongVersion." +
          selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"].objectSubType
      );
    } else if (property === "LivingSpace" || property === "PlotSize") {
      return (
        selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"][property] + " m²"
      );
    } else if (property === "PlotType") {
      return this.translocoService.translate("Properties.PlotType.LongVersion." + selectedObject.objectInformation.plotType);
    } else if (property === "Purchase" || property === "Rent") {
      return (
        this.decimalPipe.transform(selectedObject.prices.original.price, "1.0-0", this.optionService.locale()) +
        " " +
        this.popupService.unit[0]
      );
    } else {
      return selectedObject[this.popupService.mode() === PopupMode.Portfolio ? "ObjectInformationShort" : "ObjectInformation"][property];
    }
  }

  protected selectPortfolioObject(locationObject: PortfolioLocationObject) {
    this.popupService.selectedPortfolioDetails();
    this.popupService.setPortfolioObjectInfo(locationObject);
  }

  protected selectCompareObject(compareObject: CompareObject) {
    this.popupService.selectedCompareObjects.set([compareObject]);

    this.popupService.allowPopupNavigation.set(true);
    this.compareObjectService.selectedCompareObject.set(this.popupService.selectedCompareObject());
  }

  protected removeObject() {
    this.popupService.closeActivePopup();
  }

  protected navigate() {
    if (this.popupService.mode() === PopupMode.Portfolio) {
      this.popupService.goToPortfolioObjectClick.next(this.popupService.selectedPortfolioObject());
    } else if (
      this.popupService.mode() === PopupMode.ResidentialRealEstate ||
      this.popupService.mode() === PopupMode.CommercialRealEstate
    ) {
      this.popupService.goToAdaptionClick.next(this.popupService.selectedCompareObject());
    }
    this.popupService.closeActivePopup();
  }

  protected getLegendForMapFeature(color: boolean, mapFeatureValue: string): string {
    const convertedMapFeatureValue = this.conversionService.convertStringToNumber(mapFeatureValue);
    const unit =
      this.mapFeatureInfoService.mapFeatureInfo.legend[0].color === null
        ? " " + this.mapFeatureInfoService.mapFeatureInfo.legend[0].displayText
        : "";
    let value = null;
    let selectedColor = "";

    // Get value and color from legend by selected map feature
    this.mapFeatureInfoService.mapFeatureInfo.legend?.forEach((legendItem, index) => {
      if (legendItem.displayText?.includes("-")) {
        // If legend is a range (10 - 20), check if selected value is within the bounds
        if (
          convertedMapFeatureValue >= legendItem.displayText.split("-").map(e => this.conversionService.convertStringToNumber(e))[0] &&
          convertedMapFeatureValue <= legendItem.displayText.split("-").map(e => this.conversionService.convertStringToNumber(e))[1]
        ) {
          value = mapFeatureValue;
          selectedColor = legendItem.color;
        }
      } else {
        // If legend is the last object, check if selected value is equal or greater than the bound
        if (
          index == this.mapFeatureInfoService.mapFeatureInfo.legend.length - 1 &&
          convertedMapFeatureValue >= +legendItem.displayText?.match(/\d/g)
        ) {
          if (mapFeatureValue >= legendItem.displayText?.match(/\d/g)?.join("")) {
            value = mapFeatureValue;
            selectedColor = legendItem.color;
          }
        } else if (index in [0, 1] && legendItem.displayText?.match(/\d/g)) {
          // If legend is the first object, check if selected value is equal or less than the bound
          if (convertedMapFeatureValue <= +legendItem.displayText?.match(/\d/g)?.join("")) {
            value = mapFeatureValue;
            selectedColor = legendItem.color;
          }
        }
      }
    });

    // If there is no numeric range value, show text
    if (!value) {
      value =
        this.mapFeatureInfoService.mapFeatureInfo.legend.find(e => e.value === mapFeatureValue)?.displayText !== null &&
        this.mapFeatureInfoService.mapFeatureInfo.legend.find(e => e.value === mapFeatureValue)?.displayText !== undefined
          ? this.mapFeatureInfoService.mapFeatureInfo.legend.find(e => e.value === mapFeatureValue)?.displayText
          : mapFeatureValue;
      selectedColor = this.mapFeatureInfoService.mapFeatureInfo.legend.find(e => e.value === mapFeatureValue)?.color;
    }

    return color ? selectedColor : value + unit;
  }
}
